import { useEffect, useMemo, useRef, useState } from 'react';
import { search } from '../lib/search';
import type { Meta, Ref } from '../lib/types';

interface Props {
  meta: Meta;
  /** The chapter this was opened from; a bare "ESV" resolves against it. */
  context: Ref;
  heading: string;
  onSelect: (ref: Ref) => void;
  onClose: () => void;
}

export function Palette({ meta, context, heading, onSelect, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const results = useMemo(
    () => search(query, { books: meta.books, translations: meta.translations, context }),
    [query, meta, context],
  );

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (results[active]) onSelect(results[active].ref);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className='scrim'
      onPointerDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className='palette' role='dialog' aria-modal='true' aria-label={heading}>
        <input
          className='palette__input'
          autoFocus
          value={query}
          placeholder={heading}
          spellCheck={false}
          autoComplete='off'
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-activedescendant={results[active] ? `result-${active}` : undefined}
        />

        {results.length > 0 && (
          <ul className='palette__results' ref={listRef} role='listbox'>
            {results.map((result, i) => (
              <li
                key={`${result.ref.translation}.${result.ref.book}.${result.ref.chapter}`}
                id={`result-${i}`}
                role='option'
                aria-selected={i === active}
                className={`result${i === active ? ' result--active' : ''}`}
                onPointerDown={(e) => {
                  e.preventDefault();
                  onSelect(result.ref);
                }}
                onPointerEnter={() => setActive(i)}
              >
                <span className='result__ref'>{result.label}</span>
                <span className='result__translation'>
                  <span className='result__abbr'>{result.translation.abbr}</span>
                  <span className='result__name'>{result.translation.name}</span>
                </span>
              </li>
            ))}
          </ul>
        )}

        {query.trim() && results.length === 0 && (
          <p className='palette__empty'>No chapter matches “{query.trim()}”</p>
        )}

        <p className='palette__hint'>
          Try <code>john 3</code>, <code>ps 23 kjv</code>, or just <code>esv</code>{' '}
          for a parallel
        </p>
      </div>
    </div>
  );
}
